import mongoose from "mongoose";

import {
  Class,
  Enrollment,
  Notification,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_TYPE,
  type NotificationPriority,
  School,
  Student,
  Teacher,
  User,
} from "../models";
import { logger } from "../utils/logger";

/**
 * Notification Service
 * Handles automatic creation of notifications for various system events
 */
class NotificationService {
  /**
   * Create a notification for a user
   */
  async createNotification(
    userId: mongoose.Types.ObjectId | string,
    type: string,
    title: string,
    message: string,
    options?: {
      priority?: string;
      actionUrl?: string;
      metadata?: Record<string, any>;
      schoolId?: mongoose.Types.ObjectId | string;
    },
  ): Promise<void> {
    try {
      const notification = new Notification({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: options?.schoolId
          ? new mongoose.Types.ObjectId(options.schoolId)
          : undefined,
        type: type as any,
        title,
        message,
        priority: (options?.priority as any) || NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: options?.actionUrl,
        metadata: options?.metadata || {},
        isRead: false,
      });

      await notification.save();
    } catch (error: unknown) {
      logger.error("Failed to create notification:", error);
    }
  }

  /**
   * Create notifications for multiple users
   */
  async createBulkNotifications(
    userIds: (mongoose.Types.ObjectId | string)[],
    type: string,
    title: string,
    message: string,
    options?: {
      priority?: string;
      actionUrl?: string;
      metadata?: Record<string, any>;
      schoolId?: mongoose.Types.ObjectId | string;
    },
  ): Promise<void> {
    try {
      const notifications = userIds.map(
        userId =>
          new Notification({
            userId: new mongoose.Types.ObjectId(userId),
            schoolId: options?.schoolId
              ? new mongoose.Types.ObjectId(options.schoolId)
              : undefined,
            type: type as any,
            title,
            message,
            priority:
              (options?.priority as any) || NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: options?.actionUrl,
            metadata: options?.metadata || {},
            isRead: false,
          }),
      );

      await Notification.insertMany(notifications);
    } catch (error: unknown) {
      logger.error("Failed to create bulk notifications:", error);
    }
  }

  /**
   * Notify student about leave request approval/rejection
   */
  async notifyLeaveRequestStatus(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    isApproved: boolean,
    leaveRequestId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;
    const dateStr = startDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    await (isApproved
      ? this.createNotification(
          userId,
          NOTIFICATION_TYPE.LEAVE_APPROVED,
          "Leave Request Approved",
          `Your leave request for ${dateStr} has been approved`,
          {
            priority: NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: `/student/leave-requests/${leaveRequestId}`,
            metadata: {
              leaveRequestId,
              startDate,
              endDate,
            },
            schoolId,
          },
        )
      : this.createNotification(
          userId,
          NOTIFICATION_TYPE.LEAVE_REJECTED,
          "Leave Request Rejected",
          `Your leave request for ${dateStr} has been rejected`,
          {
            priority: NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: `/student/leave-requests/${leaveRequestId}`,
            metadata: {
              leaveRequestId,
              startDate,
              endDate,
            },
            schoolId,
          },
        ));
  }

  /**
   * Notify student about withdrawal request approval/rejection
   */
  async notifyWithdrawalRequestStatus(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    classId: mongoose.Types.ObjectId,
    isApproved: boolean,
    withdrawalRequestId: string,
    className: string,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;

    await (isApproved
      ? this.createNotification(
          userId,
          NOTIFICATION_TYPE.WITHDRAWAL_APPROVED,
          "Withdrawal Request Approved",
          `Your withdrawal request for ${className} has been approved`,
          {
            priority: NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: `/student/withdrawal-requests/${withdrawalRequestId}`,
            metadata: {
              withdrawalRequestId,
              classId: classId.toString(),
              className,
            },
            schoolId,
          },
        )
      : this.createNotification(
          userId,
          NOTIFICATION_TYPE.WITHDRAWAL_REJECTED,
          "Withdrawal Request Rejected",
          `Your withdrawal request for ${className} has been rejected`,
          {
            priority: NOTIFICATION_PRIORITY.MEDIUM,
            actionUrl: `/student/withdrawal-requests/${withdrawalRequestId}`,
            metadata: {
              withdrawalRequestId,
              classId: classId.toString(),
              className,
            },
            schoolId,
          },
        ));
  }

  /**
   * Notify students about new assignment
   */
  async notifyAssignmentCreated(
    classId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    assignmentId: string,
    assignmentTitle: string,
    deadline: Date,
  ): Promise<void> {
    const classItem = await Class.findById(classId);
    if (!classItem || !classItem.studentIds) return;

    const enrollments = await Enrollment.find({
      courseOfferingId: classId,
      status: "enrolled",
    });

    const studentIds = enrollments.map(
      (e: { studentId: mongoose.Types.ObjectId }) => e.studentId,
    );
    const students = await Student.find({
      _id: { $in: studentIds },
      schoolId,
    }).populate("userId");

    const userIds = students
      .map(s => (s.userId as any)?._id || s.userId)
      .filter(Boolean);

    const deadlineStr = deadline.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    await this.createBulkNotifications(
      userIds,
      NOTIFICATION_TYPE.ASSIGNMENT_CREATED,
      "New Assignment",
      `New assignment "${assignmentTitle}" has been posted. Deadline: ${deadlineStr}`,
      {
        priority: NOTIFICATION_PRIORITY.HIGH,
        actionUrl: `/student/assignments/${assignmentId}`,
        metadata: {
          assignmentId,
          classId: classId.toString(),
          className: classItem.className,
          deadline,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify student about assignment grade
   */
  async notifyAssignmentGraded(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    assignmentId: string,
    assignmentTitle: string,
    grade: number,
    totalMarks: number,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;

    await this.createNotification(
      userId,
      NOTIFICATION_TYPE.ASSIGNMENT_GRADED,
      "Assignment Graded",
      `Your assignment "${assignmentTitle}" has been graded. Score: ${grade}/${totalMarks}`,
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/student/assignments/${assignmentId}/submission`,
        metadata: {
          assignmentId,
          grade,
          totalMarks,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify students about new quiz
   */
  async notifyQuizCreated(
    classId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    quizId: string,
    quizTitle: string,
    deadline: Date,
  ): Promise<void> {
    const classItem = await Class.findById(classId);
    if (!classItem || !classItem.studentIds) return;

    const enrollments = await Enrollment.find({
      courseOfferingId: classId,
      status: "enrolled",
    });

    const studentIds = enrollments.map(
      (e: { studentId: mongoose.Types.ObjectId }) => e.studentId,
    );
    const students = await Student.find({
      _id: { $in: studentIds },
      schoolId,
    }).populate("userId");

    const userIds = students
      .map(s => (s.userId as any)?._id || s.userId)
      .filter(Boolean);

    const deadlineStr = deadline.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    await this.createBulkNotifications(
      userIds,
      NOTIFICATION_TYPE.QUIZ_CREATED,
      "New Quiz",
      `New quiz "${quizTitle}" has been posted. Deadline: ${deadlineStr}`,
      {
        priority: NOTIFICATION_PRIORITY.HIGH,
        actionUrl: `/student/quizzes/${quizId}`,
        metadata: {
          quizId,
          classId: classId.toString(),
          className: classItem.className,
          deadline,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify student about quiz grade
   */
  async notifyQuizGraded(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    quizId: string,
    quizTitle: string,
    score: number,
    totalMarks: number,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;

    await this.createNotification(
      userId,
      NOTIFICATION_TYPE.QUIZ_GRADED,
      "Quiz Graded",
      `Your quiz "${quizTitle}" has been graded. Score: ${score}/${totalMarks}`,
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/student/quizzes/${quizId}/results`,
        metadata: {
          quizId,
          score,
          totalMarks,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify student about attendance marking
   */
  async notifyAttendanceMarked(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    className: string,
    status: string,
    date: Date,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;

    let title = "Attendance Marked";
    let message = `Your attendance for ${className} has been marked as ${status}`;
    let priority: NotificationPriority = NOTIFICATION_PRIORITY.LOW;
    let notificationType = NOTIFICATION_TYPE.ATTENDANCE_MARKED;

    if (status === "late") {
      title = "Attendance Marked Late";
      message = `Your attendance for ${className} marked late`;
      priority = NOTIFICATION_PRIORITY.MEDIUM;
      notificationType = NOTIFICATION_TYPE.ATTENDANCE_MARKED;
    } else if (status === "absent") {
      title = "Attendance Marked Absent";
      message = `Your attendance for ${className} marked absent`;
      priority = NOTIFICATION_PRIORITY.MEDIUM;
      notificationType = NOTIFICATION_TYPE.ATTENDANCE_MARKED;
    }

    await this.createNotification(userId, notificationType, title, message, {
      priority,
      actionUrl: `/student/attendance`,
      metadata: {
        className,
        status,
        date,
      },
      schoolId,
    });
  }

  /**
   * Notify students about class reminder
   */
  async notifyClassReminder(
    classId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    className: string,
    reminderMessage: string,
  ): Promise<void> {
    const classItem = await Class.findById(classId);
    if (!classItem || !classItem.studentIds) return;

    const enrollments = await Enrollment.find({
      courseOfferingId: classId,
      status: "enrolled",
    });

    const studentIds = enrollments.map(
      (e: { studentId: mongoose.Types.ObjectId }) => e.studentId,
    );
    const students = await Student.find({
      _id: { $in: studentIds },
      schoolId,
    }).populate("userId");

    const userIds = students
      .map(s => (s.userId as any)?._id || s.userId)
      .filter(Boolean);

    await this.createBulkNotifications(
      userIds,
      NOTIFICATION_TYPE.CLASS_REMINDER,
      "Class Reminder",
      reminderMessage || `Your ${className} class is coming this week`,
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/student/classes/${classId.toString()}`,
        metadata: {
          classId: classId.toString(),
          className,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify about escort request (for teachers/school)
   */
  async notifyEscortRequest(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    alertId: string,
    studentName: string,
  ): Promise<void> {
    // Get school admin user
    const school = await School.findOne({ _id: schoolId });
    if (!school) return;

    const schoolUser = await User.findById(school.userId);
    if (!schoolUser) return;

    await this.createNotification(
      schoolUser._id.toString(),
      NOTIFICATION_TYPE.ESCORT_REQUEST,
      "Escort Request",
      `Escort Request has been Initiated by student ${studentName}. You want to join?`,
      {
        priority: NOTIFICATION_PRIORITY.HIGH,
        actionUrl: `/school/alerts/${alertId}`,
        metadata: {
          alertId,
          studentId: studentId.toString(),
          studentName,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify about chat/group message
   */
  async notifyChatMessage(
    userIds: (mongoose.Types.ObjectId | string)[],
    schoolId: mongoose.Types.ObjectId,
    groupName: string,
    senderName: string,
    chatId: string,
  ): Promise<void> {
    await this.createBulkNotifications(
      userIds,
      NOTIFICATION_TYPE.GROUP_MESSAGE,
      "New Group Message",
      `${groupName} has new message`,
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/chats/${chatId}`,
        metadata: {
          chatId,
          groupName,
          senderName,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify school about teacher join request
   */
  async notifyTeacherJoinRequest(
    schoolId: mongoose.Types.ObjectId,
    teacherId: mongoose.Types.ObjectId,
    teacherName: string,
    teacherEmail: string,
  ): Promise<void> {
    const school = await School.findById(schoolId);
    if (!school) return;

    const schoolUser = await User.findById(school.userId);
    if (!schoolUser) return;

    await this.createNotification(
      schoolUser._id.toString(),
      NOTIFICATION_TYPE.TEACHER_JOIN_REQUEST,
      "New Teacher Join Request",
      `${teacherName} (${teacherEmail}) has requested to join your school`,
      {
        priority: NOTIFICATION_PRIORITY.HIGH,
        actionUrl: `/school/teachers/pending`,
        metadata: {
          teacherId: teacherId.toString(),
          teacherName,
          teacherEmail,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify school about student join request
   */
  async notifyStudentJoinRequest(
    schoolId: mongoose.Types.ObjectId,
    studentId: mongoose.Types.ObjectId,
    studentName: string,
    studentEmail: string,
  ): Promise<void> {
    const school = await School.findById(schoolId);
    if (!school) return;

    const schoolUser = await User.findById(school.userId);
    if (!schoolUser) return;

    await this.createNotification(
      schoolUser._id.toString(),
      NOTIFICATION_TYPE.STUDENT_JOIN_REQUEST,
      "New Student Join Request",
      `${studentName} (${studentEmail}) has requested to join your school`,
      {
        priority: NOTIFICATION_PRIORITY.HIGH,
        actionUrl: `/school/students/pending`,
        metadata: {
          studentId: studentId.toString(),
          studentName,
          studentEmail,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify teacher/school about assignment submission
   */
  async notifyAssignmentSubmitted(
    teacherId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    studentId: mongoose.Types.ObjectId,
    studentName: string,
    assignmentId: string,
    assignmentTitle: string,
    submissionId: string,
    isLate: boolean,
  ): Promise<void> {
    const teacher = await Teacher.findById(teacherId).populate("userId");
    if (!teacher) return;

    const teacherUserId = (teacher.userId as any)?._id || teacher.userId;

    await this.createNotification(
      teacherUserId,
      isLate
        ? NOTIFICATION_TYPE.ASSIGNMENT_LATE_SUBMITTED
        : NOTIFICATION_TYPE.ASSIGNMENT_SUBMITTED,
      isLate ? "Late Assignment Submitted" : "Assignment Submitted",
      `${studentName} has submitted ${isLate ? "a late" : "the"} assignment: ${assignmentTitle}`,
      {
        priority: isLate
          ? NOTIFICATION_PRIORITY.HIGH
          : NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/teacher/assignments/${assignmentId}/submissions/${submissionId}`,
        metadata: {
          assignmentId,
          studentId: studentId.toString(),
          studentName,
          submissionId,
          isLate,
        },
        schoolId,
      },
    );

    // Also notify school admin
    const school = await School.findById(schoolId);
    if (school) {
      const schoolUser = await User.findById(school.userId);
      if (schoolUser) {
        await this.createNotification(
          schoolUser._id.toString(),
          isLate
            ? NOTIFICATION_TYPE.ASSIGNMENT_LATE_SUBMITTED
            : NOTIFICATION_TYPE.ASSIGNMENT_SUBMITTED,
          isLate ? "Late Assignment Submitted" : "Assignment Submitted",
          `${studentName} submitted ${isLate ? "a late" : "an"} assignment: ${assignmentTitle}`,
          {
            priority: isLate
              ? NOTIFICATION_PRIORITY.MEDIUM
              : NOTIFICATION_PRIORITY.LOW,
            actionUrl: `/school/assignments/${assignmentId}/submissions`,
            metadata: {
              assignmentId,
              studentId: studentId.toString(),
              studentName,
              submissionId,
              isLate,
            },
            schoolId,
          },
        );
      }
    }
  }

  /**
   * Notify students about upcoming attendance (class reminder)
   */
  async notifyUpcomingAttendance(
    classId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    className: string,
    classTime: Date,
    minutesBefore = 30,
  ): Promise<void> {
    const classItem = await Class.findById(classId);
    if (!classItem || !classItem.studentIds) return;

    const enrollments = await Enrollment.find({
      courseOfferingId: classId,
      status: "enrolled",
    });

    const studentIds = enrollments.map(
      (e: { studentId: mongoose.Types.ObjectId }) => e.studentId,
    );
    const students = await Student.find({
      _id: { $in: studentIds },
      schoolId,
    }).populate("userId");

    const userIds = students
      .map(s => (s.userId as any)?._id || s.userId)
      .filter(Boolean);

    const timeStr = classTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    await this.createBulkNotifications(
      userIds,
      NOTIFICATION_TYPE.ATTENDANCE_UPCOMING,
      "Upcoming Class Attendance",
      `Your ${className} class starts at ${timeStr}. Attendance will be marked soon.`,
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/student/classes/${classId.toString()}`,
        metadata: {
          classId: classId.toString(),
          className,
          classTime,
          minutesBefore,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify user about customer support message
   */
  async notifySupportMessage(
    userId: mongoose.Types.ObjectId,
    ticketId: string,
    message: string,
    isFromSupport: boolean,
  ): Promise<void> {
    await this.createNotification(
      userId,
      NOTIFICATION_TYPE.SUPPORT_MESSAGE,
      isFromSupport ? "Support Team Replied" : "Your Support Message Received",
      isFromSupport
        ? `Support team replied to your ticket: ${message.slice(0, 100)}${message.length > 100 ? "..." : ""}`
        : "Your support message has been received. We'll respond shortly.",
      {
        priority: NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: `/support/tickets/${ticketId}`,
        metadata: {
          ticketId,
          isFromSupport,
        },
      },
    );
  }

  /**
   * Notify user about support ticket status
   */
  async notifySupportTicketStatus(
    userId: mongoose.Types.ObjectId,
    ticketId: string,
    status: "created" | "replied" | "resolved",
    ticketSubject: string,
  ): Promise<void> {
    let type: string = NOTIFICATION_TYPE.SUPPORT_TICKET_CREATED;
    let title = "Support Ticket Created";
    let message = `Your support ticket "${ticketSubject}" has been created`;
    let priority: string = NOTIFICATION_PRIORITY.MEDIUM;

    if (status === "replied") {
      type = NOTIFICATION_TYPE.SUPPORT_TICKET_REPLIED;
      title = "Support Team Replied";
      message = `Support team replied to your ticket: ${ticketSubject}`;
      priority = NOTIFICATION_PRIORITY.HIGH;
    } else if (status === "resolved") {
      type = NOTIFICATION_TYPE.SUPPORT_TICKET_RESOLVED;
      title = "Support Ticket Resolved";
      message = `Your support ticket "${ticketSubject}" has been resolved`;
      priority = NOTIFICATION_PRIORITY.MEDIUM;
    }

    await this.createNotification(userId, type, title, message, {
      priority,
      actionUrl: `/support/tickets/${ticketId}`,
      metadata: {
        ticketId,
        status,
        ticketSubject,
      },
    });
  }

  /**
   * Notify teacher about approval status
   */
  async notifyTeacherApprovalStatus(
    teacherId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    schoolName: string,
    isApproved: boolean,
    reason?: string,
  ): Promise<void> {
    const teacher = await Teacher.findById(teacherId).populate("userId");
    if (!teacher) return;

    const userId = (teacher.userId as any)?._id || teacher.userId;

    await this.createNotification(
      userId,
      isApproved
        ? NOTIFICATION_TYPE.TEACHER_APPROVED
        : NOTIFICATION_TYPE.TEACHER_REJECTED,
      isApproved ? "Application Approved" : "Application Status",
      isApproved
        ? `Your application to join ${schoolName} has been approved! You can now access the platform.`
        : `Your application to join ${schoolName} was not approved. ${reason || "Please contact the school for more information."}`,
      {
        priority: isApproved
          ? NOTIFICATION_PRIORITY.HIGH
          : NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: isApproved ? "/teacher/dashboard" : "/teacher/profile",
        metadata: {
          schoolId: schoolId.toString(),
          schoolName,
          isApproved,
          reason,
        },
        schoolId,
      },
    );
  }

  /**
   * Notify student about approval status
   */
  async notifyStudentApprovalStatus(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    schoolName: string,
    isApproved: boolean,
    reason?: string,
  ): Promise<void> {
    const student = await Student.findById(studentId).populate("userId");
    if (!student) return;

    const userId = (student.userId as any)?._id || student.userId;

    await this.createNotification(
      userId,
      isApproved
        ? NOTIFICATION_TYPE.STUDENT_APPROVED
        : NOTIFICATION_TYPE.STUDENT_REJECTED,
      isApproved ? "Enrollment Approved" : "Enrollment Status",
      isApproved
        ? `Your enrollment at ${schoolName} has been approved! Welcome to the school.`
        : `Your enrollment at ${schoolName} was not approved. ${reason || "Please contact the school for more information."}`,
      {
        priority: isApproved
          ? NOTIFICATION_PRIORITY.HIGH
          : NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: isApproved ? "/student/dashboard" : "/student/profile",
        metadata: {
          schoolId: schoolId.toString(),
          schoolName,
          isApproved,
          reason,
        },
        schoolId,
      },
    );
  }

  async notifyLeaveAppealSubmitted(
    leaveRequestId: string,
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    teacherUserIds: string[],
  ): Promise<void> {
    const student = await Student.findById(studentId).select("fullName userId");
    if (!student) return;

    await this.createNotification(
      student.userId,
      NOTIFICATION_TYPE.LEAVE_APPEAL_SUBMITTED,
      "Leave Appeal Submitted",
      "Your leave appeal has been submitted for review",
      {
        metadata: { leaveRequestId },
        schoolId,
        actionUrl: `/student/leave-requests/${leaveRequestId}`,
      },
    );

    if (teacherUserIds.length > 0) {
      await this.createBulkNotifications(
        teacherUserIds,
        NOTIFICATION_TYPE.LEAVE_APPEAL_SUBMITTED,
        "Leave Appeal Received",
        `${student.fullName} submitted a leave appeal`,
        {
          metadata: { leaveRequestId, studentId: studentId.toString() },
          schoolId,
          priority: NOTIFICATION_PRIORITY.HIGH,
        },
      );
    }
  }

  async notifyLeaveAppealResolved(
    studentId: mongoose.Types.ObjectId,
    schoolId: mongoose.Types.ObjectId,
    leaveRequestId: string,
    approved: boolean,
  ): Promise<void> {
    const student = await Student.findById(studentId).select("userId");
    if (!student) return;

    await this.createNotification(
      student.userId,
      approved
        ? NOTIFICATION_TYPE.LEAVE_APPEAL_APPROVED
        : NOTIFICATION_TYPE.LEAVE_APPEAL_REJECTED,
      approved ? "Leave Appeal Approved" : "Leave Appeal Rejected",
      approved
        ? "Your leave appeal has been approved"
        : "Your leave appeal has been rejected",
      {
        metadata: { leaveRequestId },
        schoolId,
        actionUrl: `/student/leave-requests/${leaveRequestId}`,
      },
    );
  }
}

export const notificationService = new NotificationService();
